<script setup lang="ts">
import {
  CenteredModal,
  IconEmoji,
  ImageGrid,
  notifyScreenKeyboardHidden,
  notifyScreenKeyboardShown,
  SlideTransition,
} from '#shared'
import { computed, toRef, useId, useTemplateRef, watch } from 'vue'
import { EmojiSelect, useEmojiController } from '#features/emoji-selection'
import type { AttachmentsController } from '../../lib/use-attachments-controller'
import { useTextLimitController } from '../../lib/use-text-limit-controller'
import AttachmentsModalFile from './attachments-modal-file.vue'
import AttachmentsModalImage from './attachments-modal-image.vue'

interface AttachmentModalProps {
  attachmentsController: AttachmentsController
  caption: string
}

const { attachmentsController, caption } = defineProps<AttachmentModalProps>()

const emit = defineEmits<{ 'caption-change': [caption: string]; 'send': []; 'close': [] }>()

const inputRowCount = computed(() => caption.split('\n').length)
const textLimitController = useTextLimitController(toRef(() => caption))

const captionInputId = useId()
const captionInputEl = useTemplateRef('captionInputEl')
const captionInputWrapEl = useTemplateRef('captionInputWrapEl')

const emojiController = useEmojiController(captionInputEl)
watch(
  () => attachmentsController.attachmentsModalDisclosure.isOpen,
  isOpen => {
    if (!isOpen) emojiController.emojiSelectDisclosure.close()
  }
)

const onCaptionInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement
  emit('caption-change', target.value)
}

const canSend = computed(() => attachmentsController.uploadState === 'finished')

const onClose = () => {
  emit('close')
}

const onSend = () => emit('send')
</script>

<template>
  <CenteredModal
    :modalClass="$style.root"
    :isOpen="attachmentsController.attachmentsModalDisclosure.isOpen"
    @close="onClose"
  >
    <div :class="$style.content">
      <div :class="$style.title">Send a file</div>

      <div :class="$style.uploads">
        <ImageGrid v-if="attachmentsController.imageInfos.length > 0" :imageInfos="attachmentsController.imageInfos">
          <template v-slot="{ imageInfo }">
            <AttachmentsModalImage
              :imageInfo="imageInfo"
              :upload-state="imageInfo.meta?.meta?.uploadState"
              @delete="attachmentsController.deleteAttachment(imageInfo.meta!)"
              @reupload="attachmentsController.uploadFile(imageInfo.file!)"
            />
          </template>
        </ImageGrid>

        <ul :class="$style.fileList" v-if="attachmentsController.fileAttachments.length > 0">
          <AttachmentsModalFile
            v-for="fileAttachment in attachmentsController.fileAttachments"
            :key="fileAttachment.url"
            :attachment="fileAttachment"
            @delete="attachmentsController.deleteAttachment(fileAttachment)"
            @reupload="attachmentsController.uploadFile(fileAttachment.meta?.file!)"
          />
        </ul>
      </div>

      <label :class="$style.captionInputLabel" :for="captionInputId"> Caption </label>

      <div :class="$style.captionInputWrap" ref="captionInputWrapEl">
        <textarea
          :class="$style.captionInput"
          :id="captionInputId"
          :value="caption"
          :rows="inputRowCount"
          :maxlength="textLimitController.maxTextLength"
          placeholder="Enter caption..."
          ref="captionInputEl"
          @input="onCaptionInput"
          @focus="notifyScreenKeyboardShown"
          @blur="notifyScreenKeyboardHidden"
        />

        <button :class="$style.inputAction" @click="emojiController.emojiSelectDisclosure.toggle">
          <IconEmoji />
        </button>

        <div
          v-if="textLimitController.isLengthLimitShown"
          :class="[$style.length, { [$style.lengthWarning]: textLimitController.isLengthLimitWarningShown }]"
        >
          {{ caption.length }} / {{ textLimitController.maxTextLength }}
        </div>
      </div>

      <div :class="$style.actions">
        <button :class="[$style.action, $style.actionAdd]" @click="attachmentsController.showFileDialog">
          Add file
        </button>
        <button :class="[$style.action, $style.actionCancel]" @click="onClose">Cancel</button>
        <button
          :class="[$style.action, $style.actionEmphasized, $style.actionSend]"
          :disabled="!canSend"
          @click="onSend"
        >
          Send
        </button>
      </div>
    </div>
  </CenteredModal>

  <SlideTransition :offsetY="10">
    <EmojiSelect
      :isOpen="emojiController.emojiSelectDisclosure.isOpen"
      :targetEl="captionInputWrapEl"
      verticalPosition="top-outside"
      horizontalPosition="right-inside"
      size="small"
      @select="emojiController.onEmojiSelect"
    />
  </SlideTransition>
</template>

<style lang="scss" module>
@use '../mixins.scss' as *;
@use '#mixins/media' as media;
@use '#mixins/button' as button;

.root {
  width: calc(100vw - 40px);
  max-width: 560px;
}

.content {
  display: flex;
  flex-direction: column;
  padding: 30px;
  max-height: calc(100vh - 160px);

  @include media.mobile {
    padding: 16px;
  }
}

.title {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 20px;

  @include media.mobile {
    font-size: 16px;
  }
}

.uploads {
  margin-bottom: 35px;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fileList {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.captionInputLabel {
  font-weight: 500;
  margin-bottom: 5px;
  cursor: default;

  @include media.mobile {
    font-size: 14px;
  }
}

.captionInputWrap {
  border-bottom: 2px solid #4a8dff;
  display: flex;
  margin-bottom: 20px;
  position: relative;
}

.captionInput {
  padding: 9px 0;
  min-width: 0;
  flex-grow: 1;
  resize: none;
  max-height: 130px;

  &:focus {
    outline: none;
  }

  @include media.mobile {
    font-size: 14px;
  }
}

.inputAction {
  @include input-action;

  & {
    margin-top: 5px;
  }
}

.actions {
  display: flex;
  gap: 30px;
}

.action {
  @include button.text;
}

.actionEmphasized {
  @include button.secondary;
}

.actionCancel {
  margin-left: auto;
}

.length {
  font-size: 13px;
  text-align: right;
  white-space: nowrap;
  position: absolute;
  bottom: -20px;
  right: 0;
}

.lengthWarning {
  color: var(--sc-error);
}
</style>
